// src/pages/provider/ProviderGalleryPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  Plus,
  Image,
  X,
  Trash2,
  Pencil,
  MoreVertical,
  CloudUpload,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Loader
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/ProviderGalleryPage.module.css';
import {
  getGalleryImages,
  addGalleryImages,
  removeGalleryImage,
  updateGalleryImage,
  selectGalleryImages,
  selectGalleryLoading,
  selectGalleryUploading,
  selectGalleryDeleting,
  selectGalleryError,
  selectGalleryUploadSuccess,
  selectGalleryDeleteSuccess,
  resetGalleryState,
  resetUploadState,
  resetDeleteState,
  clearGalleryErrors
} from '../../store/slices/providerGalleryServiceSlice';
import { selectUser, selectIsProvider } from '../../store/slices/authSlice';
import { selectProfile } from '../../store/slices/profileSlice';
import { selectCurrentProvider } from '../../store/slices/providerSlice';

const ProviderGalleryPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentLanguage } = useLanguage();

  // Redux selectors
  const user = useSelector(selectUser);
  const isProvider = useSelector(selectIsProvider);
  const profile = useSelector(selectProfile);
  const provider = useSelector(selectCurrentProvider);
  
  // Gallery selectors
  const galleryImagesFromSlice = useSelector(selectGalleryImages);
  const isLoading = useSelector(selectGalleryLoading);
  const isUploading = useSelector(selectGalleryUploading);
  const isDeleting = useSelector(selectGalleryDeleting);
  const error = useSelector(selectGalleryError);
  const uploadSuccess = useSelector(selectGalleryUploadSuccess);
  const deleteSuccess = useSelector(selectGalleryDeleteSuccess);

  // Local state
  const [refreshing, setRefreshing] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [caption, setCaption] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [imagesPerPage] = useState(12);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [imageLoading, setImageLoading] = useState({});
  const [retryCount, setRetryCount] = useState({});
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const uploadSuccessHandled = useRef(false);
  const uploadTriggeredRef = useRef(false);
  const fileInputRef = useRef(null);

  // Translations
  const { translatedText: galleryTitle } = useTranslation('Gallery');
  const { translatedText: noImages } = useTranslation('No images in gallery');
  const { translatedText: uploadImage } = useTranslation('Upload Image');
  const { translatedText: deleteImage } = useTranslation('Delete Image');
  const { translatedText: editImage } = useTranslation('Edit Image');
  const { translatedText: captionLabel } = useTranslation('Caption');
  const { translatedText: addCaption } = useTranslation('Add caption...');
  const { translatedText: chooseImage } = useTranslation('Choose Image');
  const { translatedText: cancel } = useTranslation('Cancel');
  const { translatedText: save } = useTranslation('Save');
  const { translatedText: deleteConfirm } = useTranslation('Are you sure you want to delete this image?');
  const { translatedText: uploadSuccessMessage } = useTranslation('Image uploaded successfully');
  const { translatedText: deleteSuccessMessage } = useTranslation('Image deleted successfully');
  const { translatedText: errorTitle } = useTranslation('Error');
  const { translatedText: tryAgain } = useTranslation('Please try again');
  const { translatedText: refresh } = useTranslation('Refresh');
  const { translatedText: loading } = useTranslation('Loading...');
  const { translatedText: imageLoadError } = useTranslation('Failed to load image');
  
  // ======================================================
  // FOOTER TRANSLATIONS
  // ======================================================
  const { translatedText: homeTitle } = useTranslation('Haitian Services');
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboardLabel } = useTranslation('Dashboard');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide, One Service at a Time');
  const { translatedText: madeWithLove } = useTranslation('Made with ❤️ in Haiti');

  // The gallery endpoint is the source of truth. Do not prefer provider/profile
  // copies because they can contain stale or differently shaped image objects.
  const galleryImages = Array.isArray(galleryImagesFromSlice)
    ? galleryImagesFromSlice
    : [];

  // Normalize Cloudinary URLs before giving them to the browser. This handles
  // both Markdown-wrapped URLs and the invalid `t_w_300,...` thumbnail form.
  const normalizeImageUrl = useCallback((value) => {
    if (!value || typeof value !== 'string') return '';

    let url = value.trim();

    // If an API/client accidentally returns a Markdown link, extract the URL.
    const markdownMatch = url.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (markdownMatch) {
      url = markdownMatch[2].trim();
    }

    // Cloudinary named transformations use `t_<name>`. The value currently
    // returned for thumbnails is an inline transformation and must not have
    // the `t_` prefix.
    url = url.replace(
      '/image/upload/t_w_300,h_200,c_thumb,q_auto/',
      '/image/upload/w_300,h_200,c_thumb,q_auto/'
    );

    return url;
  }, []);

  // Get provider ID
  const providerId = profile?.providerProfile?._id || 
                     user?.providerId || 
                     user?.id || 
                     user?._id || 
                     provider?.id ||
                     provider?._id;

  // Load gallery data
  const loadGallery = useCallback(async () => {
    if (!providerId) {
      console.warn('[Gallery] No provider ID available');
      return;
    }

    try {
      console.log('[Gallery] Loading gallery for provider:', providerId);
      const result = await dispatch(getGalleryImages({ providerId })).unwrap();
      console.log('[Gallery] Gallery loaded successfully:', result);
      return result;
    } catch (err) {
      console.error('[Gallery] Failed to load gallery:', err);
      return null;
    }
  }, [dispatch, providerId]);

  // Initial load - only once
  useEffect(() => {
    let isMounted = true;
    
    const initialLoad = async () => {
      if (providerId && isMounted) {
        await loadGallery();
      }
    };
    
    initialLoad();
    
    return () => {
      isMounted = false;
    };
  }, [providerId, loadGallery]);



  // Handle delete success
  useEffect(() => {
    if (deleteSuccess) {
      setShowDeleteConfirm(false);
      setImageToDelete(null);
      dispatch(resetDeleteState());
      loadGallery();
    }
  }, [deleteSuccess, dispatch, loadGallery]);

  // Handle errors
  useEffect(() => {
    if (error) {
      console.error('[Gallery] Error:', error);
      dispatch(clearGalleryErrors());
    }
  }, [error, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetGalleryState());
      uploadSuccessHandled.current = false;
      uploadTriggeredRef.current = false;
    };
  }, [dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadGallery();
    setRefreshing(false);
  };

  const handleFileSelect = () => {
    // Reset file input value to allow selecting the same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setSelectedFiles(prev => [...prev, ...fileArray]);
    }
    // Reset the input value to allow selecting the same files again
    event.target.value = '';
  };

const handleUploadImages = async () => {
  if (uploadInProgress || isUploading || uploadTriggeredRef.current) {
    console.log('[Gallery] Upload already in progress, skipping...');
    return;
  }

  if (!providerId) {
    alert('Provider ID not found');
    return;
  }

  if (selectedFiles.length === 0) {
    alert('Please select at least one image');
    return;
  }

  const filesToUpload = [...selectedFiles];

  uploadTriggeredRef.current = true;
  setUploadInProgress(true);

  try {
    console.log(
      '[Gallery] Starting upload:',
      filesToUpload.length,
      'images'
    );

    // 1. Upload images
    const uploadResult = await dispatch(
      addGalleryImages({
        providerId,
        files: filesToUpload,
        options: {
          category: 'work_example'
        }
      })
    ).unwrap();

    console.log('[Gallery] Upload successful:', uploadResult);

    // 2. Clear selected files
    setSelectedFiles([]);

    // 3. IMPORTANT:
    // Fetch the gallery again from the backend.
    // This replaces Redux with the actual database state.
    console.log('[Gallery] Refreshing gallery after upload...');

    const galleryResult = await dispatch(
      getGalleryImages({
        providerId,
        page: 1,
        limit: 20
      })
    ).unwrap();

    console.log(
      '[Gallery] Gallery refreshed:',
      galleryResult?.data?.length || 0,
      'images'
    );

    // 4. Reset upload state
    dispatch(resetUploadState());

  } catch (err) {
    console.error('[Gallery] Upload failed:', err);

    alert(
      err?.message ||
      err?.error?.message ||
      'Upload failed. Please try again.'
    );
  } finally {
    setUploadInProgress(false);
    uploadTriggeredRef.current = false;
  }
};

  const handleDeleteImage = (imageId) => {
    setImageToDelete(imageId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!imageToDelete || !providerId) return;

    try {
      await dispatch(removeGalleryImage({
        providerId,
        imageId: imageToDelete
      })).unwrap();
      console.log('[Gallery] Image deleted successfully');
    } catch (err) {
      console.error('[Gallery] Delete failed:', err);
      alert(err?.message || 'Delete failed. Please try again.');
      setShowDeleteConfirm(false);
    }
  };

  const handleUpdateImage = async (imageId, updateData) => {
    if (!providerId) return;

    try {
      await dispatch(updateGalleryImage({
        providerId,
        imageId,
        updateData
      })).unwrap();
      setShowEditModal(false);
      setEditingImage(null);
      setCaption('');
      loadGallery();
    } catch (err) {
      console.error('[Gallery] Update failed:', err);
      alert(err?.message || 'Update failed. Please try again.');
    }
  };

  const openEditModal = (image) => {
    setEditingImage(image);
    setCaption(image.caption || '');
    setShowEditModal(true);
  };

  const openImageModal = (imageId) => {
    setSelectedImageId(imageId);
    setShowImageModal(true);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleImageError = (imageId) => {
    console.log('[Gallery] Image failed to load:', imageId);
    setImageLoadErrors(prev => ({ ...prev, [imageId]: true }));
    setImageLoading(prev => ({ ...prev, [imageId]: false }));
  };

  const handleImageLoad = (imageId) => {
    console.log('[Gallery] Image loaded successfully:', imageId);
    setImageLoading(prev => ({ ...prev, [imageId]: false }));
    setImageLoadErrors(prev => ({ ...prev, [imageId]: false }));
  };

  const handleImageLoadStart = (imageId) => {
    setImageLoading(prev => ({ ...prev, [imageId]: true }));
  };

  const retryImageLoad = (imageId) => {
    setImageLoadErrors(prev => ({ ...prev, [imageId]: false }));
    setRetryCount(prev => ({ ...prev, [imageId]: (prev[imageId] || 0) + 1 }));
    // Force reload by updating the key
    const img = document.querySelector(`img[data-image-id="${imageId}"]`);
    if (img) {
      const src = img.src;
      img.src = '';
      setTimeout(() => {
        img.src = src + (src.includes('?') ? '&' : '?') + 'retry=' + Date.now();
      }, 100);
    }
  };

  // Pagination
  const indexOfLastImage = currentPage * imagesPerPage;
  const indexOfFirstImage = indexOfLastImage - imagesPerPage;
  const currentImages = galleryImages.slice(indexOfFirstImage, indexOfLastImage);
  const totalPages = Math.ceil(galleryImages.length / imagesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderImageItem = (item) => {
    const imageId = item._id || item.id || Date.now().toString();
    const hasError = imageLoadErrors[imageId];
    const isLoadingImage = imageLoading[imageId];
    const retries = retryCount[imageId] || 0;
    
    // Get the best available URL - prefer thumbnail for grid view
    const imageUrl = normalizeImageUrl(item.url || item.thumbnail);
    const fullImageUrl = normalizeImageUrl(item.url || item.thumbnail);
    const captionText = item.caption || item.title || '';

    return (
      <div
        key={imageId}
        className={styles.imageCard}
        onClick={() => openImageModal(imageId)}
      >
        {isLoadingImage && !hasError && (
          <div className={styles.imageLoadingPlaceholder}>
            <Loader size={32} className={styles.spinning} color={colors.primary} />
          </div>
        )}
        {hasError ? (
          <div className={styles.imageErrorPlaceholder}>
            <AlertCircle size={40} color={colors.gray500} />
            <span className={styles.imageErrorText}>{imageLoadError}</span>
            {retries < 3 && (
              <button 
                className={styles.retryButton}
                onClick={(e) => {
                  e.stopPropagation();
                  retryImageLoad(imageId);
                }}
              >
                Retry
              </button>
            )}
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={captionText || 'Gallery image'}
            className={styles.imageThumbnail}
            loading="lazy"
            data-image-id={imageId}
            onLoadStart={() => handleImageLoadStart(imageId)}
            onLoad={() => handleImageLoad(imageId)}
            onError={() => handleImageError(imageId)}
          />
        )}
        <button
          className={styles.imageOptionsButton}
          onClick={(e) => {
            e.stopPropagation();
            openEditModal(item);
          }}
          aria-label="Edit image"
        >
          <MoreVertical size={20} color="#ffffff" />
        </button>
        {captionText && !hasError && (
          <div className={styles.imageCaptionOverlay}>
            <span className={styles.imageCaptionText}>{captionText}</span>
          </div>
        )}
        <button
          className={styles.imageDeleteButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteImage(imageId);
          }}
          aria-label="Delete image"
        >
          <Trash2 size={16} color="#ffffff" />
        </button>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <Image size={80} className={styles.emptyIcon} />
      <h3 className={styles.emptyStateTitle}>{noImages}</h3>
      <p className={styles.emptyStateSubtitle}>
        Upload your work examples to showcase your skills
      </p>
      <button
        className={styles.uploadButton}
        onClick={handleFileSelect}
      >
        <CloudUpload size={20} />
        <span>{uploadImage}</span>
      </button>
    </div>
  );

  // Check if we have any images
  const hasImages = galleryImages && galleryImages.length > 0;

  return (
    <div className={styles.container}>
      {/* Hidden file input */}
      <input
        type="file"
        id="fileInput"
        ref={fileInputRef}
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backButton}
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className={styles.headerTitle}>{galleryTitle}</h1>
          <span className={styles.imageCount}>
            {galleryImages.length} {galleryImages.length === 1 ? 'image' : 'images'}
          </span>
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={18} className={refreshing ? styles.spinning : ''} />
            <span>{refresh}</span>
          </button>
          <button
            className={styles.addButton}
            onClick={handleFileSelect}
          >
            <Plus size={20} />
            <span>{uploadImage}</span>
          </button>
        </div>
      </div>

      {/* Upload Preview Area */}
      {selectedFiles.length > 0 && (
        <div className={styles.uploadPreview}>
          <div className={styles.uploadPreviewHeader}>
            <h4>{selectedFiles.length} image{selectedFiles.length > 1 ? 's' : ''} selected</h4>
            <button
              className={styles.clearFilesButton}
              onClick={() => {
                setSelectedFiles([]);
                uploadTriggeredRef.current = false;
              }}
            >
              <X size={18} />
              Clear all
            </button>
          </div>
          <div className={styles.uploadPreviewGrid}>
            {selectedFiles.map((file, index) => (
              <div key={index} className={styles.previewItem}>
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Preview ${index + 1}`}
                  className={styles.previewThumbnail}
                />
                <button
                  className={styles.removePreviewButton}
                  onClick={() => removeSelectedFile(index)}
                >
                  <X size={16} />
                </button>
                <div className={styles.previewFileName}>
                  {file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name}
                </div>
              </div>
            ))}
          </div>
          <div className={styles.uploadPreviewActions}>
            <button
              className={styles.cancelUploadButton}
              onClick={() => {
                setSelectedFiles([]);
                uploadTriggeredRef.current = false;
              }}
            >
              {cancel}
            </button>
            <button
              className={styles.confirmUploadButton}
              onClick={handleUploadImages}
              disabled={isUploading || uploadInProgress}
            >
              {isUploading || uploadInProgress ? (
                <>
                  <span className={styles.spinnerSmall}></span>
                  Uploading...
                </>
              ) : (
                <>
                  <CloudUpload size={18} />
                  Upload {selectedFiles.length} image{selectedFiles.length > 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Gallery Grid */}
      {isLoading && galleryImages.length === 0 ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>{loading}</p>
        </div>
      ) : (
        <>
          {hasImages ? (
            <div className={styles.galleryGrid}>
              {currentImages.map(renderImageItem)}
            </div>
          ) : (
            renderEmptyState()
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.paginationButton}
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`${styles.paginationButton} ${currentPage === page ? styles.active : ''}`}
                  onClick={() => paginate(page)}
                >
                  {page}
                </button>
              ))}
              <button
                className={styles.paginationButton}
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}

      {/* ======================================================
          FOOTER - FULLY TRANSLATED
          ====================================================== */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            {/* Brand Column */}
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            {/* Quick Links - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            {/* For Providers - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboardLabel}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            {/* Legal - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © 2026 {homeTitle}. {madeWithLove}
            </div>
          </div>
        </div>
      </footer>

      {/* Image Detail Modal */}
      {showImageModal && selectedImageId && (
        <div className={styles.modalOverlay} onClick={() => setShowImageModal(false)}>
          <div className={styles.imageModalContent} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.closeModalButton}
              onClick={() => setShowImageModal(false)}
            >
              <X size={30} />
            </button>
            {(() => {
              const image = galleryImages.find(img => {
                const imgId = img._id || img.id;
                return imgId === selectedImageId;
              });
              return image ? (
                <>
                  <img
                    src={normalizeImageUrl(image.url || image.thumbnail)}
                    alt={image.caption || 'Gallery image'}
                    className={styles.fullImage}
                    onError={(e) => {
                      // Try fallback to thumbnail if full image fails
                      const fallbackUrl = normalizeImageUrl(image.thumbnail);
                      if (fallbackUrl && e.target.src !== fallbackUrl) {
                        e.target.src = fallbackUrl;
                      } else {
                        e.target.alt = 'Failed to load image';
                        e.target.style.display = 'none';
                        // Show error message
                        const container = e.target.parentElement;
                        const errorDiv = document.createElement('div');
                        errorDiv.className = styles.imageModalError;
                        errorDiv.innerHTML = 'Failed to load image';
                        container.appendChild(errorDiv);
                      }
                    }}
                  />
                  <div className={styles.imageDetails}>
                    <p className={styles.imageDetailCaption}>
                      {image.caption || 'No caption'}
                    </p>
                    <div className={styles.imageDetailActions}>
                      <button
                        className={styles.imageDetailAction}
                        onClick={() => {
                          setShowImageModal(false);
                          openEditModal(image);
                        }}
                      >
                        <Pencil size={18} />
                        <span>{editImage}</span>
                      </button>
                      <button
                        className={`${styles.imageDetailAction} ${styles.imageDetailActionDanger}`}
                        onClick={() => {
                          setShowImageModal(false);
                          handleDeleteImage(selectedImageId);
                        }}
                      >
                        <Trash2 size={18} />
                        <span>{deleteImage}</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {/* Edit Image Modal */}
      {showEditModal && editingImage && (
        <div className={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div className={styles.editModalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.editModalHeader}>
              <h2 className={styles.editModalTitle}>{editImage}</h2>
              <button
                className={styles.modalCloseButton}
                onClick={() => {
                  setShowEditModal(false);
                  setEditingImage(null);
                  setCaption('');
                }}
              >
                <X size={24} />
              </button>
            </div>
            <div className={styles.editModalBody}>
              <img
                src={normalizeImageUrl(editingImage.url || editingImage.thumbnail)}
                alt={editingImage.caption || 'Image preview'}
                className={styles.editImagePreview}
                onError={(e) => {
                  e.target.src = normalizeImageUrl(editingImage.url || editingImage.thumbnail);
                }}
              />
              <div className={styles.editInputGroup}>
                <label className={styles.editLabel}>{captionLabel}</label>
                <input
                  type="text"
                  className={styles.editInput}
                  placeholder={addCaption}
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                />
              </div>
              <div className={styles.editModalActions}>
                <button
                  className={`${styles.editModalButton} ${styles.editModalButtonCancel}`}
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingImage(null);
                    setCaption('');
                  }}
                >
                  {cancel}
                </button>
                <button
                  className={`${styles.editModalButton} ${styles.editModalButtonSave}`}
                  onClick={() => handleUpdateImage(editingImage._id, { caption })}
                >
                  {save}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteConfirm(false)}>
          <div className={styles.confirmModalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmModalIcon}>
              <Trash2 size={48} className={styles.confirmIconDanger} />
            </div>
            <h3 className={styles.confirmModalTitle}>{deleteImage}</h3>
            <p className={styles.confirmModalText}>{deleteConfirm}</p>
            <div className={styles.confirmModalActions}>
              <button
                className={`${styles.confirmModalButton} ${styles.confirmModalButtonCancel}`}
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                {cancel}
              </button>
              <button
                className={`${styles.confirmModalButton} ${styles.confirmModalButtonDanger}`}
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className={styles.spinnerSmall}></span>
                    Deleting...
                  </>
                ) : (
                  deleteImage
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderGalleryPage;